import type { TFunction } from "i18next";
import { z } from "zod";
import type {
  DocumentDetailsRelease,
  DocumentReleaseSummary,
} from "~/api/types";
import { DocumentArtifactStatus, type ReviewPeriod } from "~/config/types";
import i18n from "~/i18n";

// Keep the length limit in sync with documentNameSchema in
// api/src/v2/features/documents/document.schema.ts.
export const DOCUMENT_NAME_MAX_LENGTH = 255;

export const documentNameSchema = z
  .string({ error: () => i18n.t(($) => $.validations.required) })
  .trim()
  .min(1, { error: () => i18n.t(($) => $.validations.required) })
  .max(DOCUMENT_NAME_MAX_LENGTH, {
    error: () =>
      i18n.t(($) => $.validations.maxLength, {
        max: DOCUMENT_NAME_MAX_LENGTH,
      }),
  });

export const reviewPeriodInMonths: Record<ReviewPeriod, number> = {
  "3 MONTH": 3,
  "6 MONTH": 6,
  "1 YEAR": 12,
  "3 YEAR": 12 * 3,
  "5 YEAR": 12 * 5,
};

export function getReviewPeriodLabel(
  period: string | null,
  t: TFunction,
): string | null {
  if (!period) return null;
  const labels: Record<ReviewPeriod, string> = {
    "3 MONTH": t(($) => $.commons.document.reviewPeriods.every3months),
    "6 MONTH": t(($) => $.commons.document.reviewPeriods.every6months),
    "1 YEAR": t(($) => $.commons.document.reviewPeriods.yearly),
    "3 YEAR": t(($) => $.commons.document.reviewPeriods.every3years),
    "5 YEAR": t(($) => $.commons.document.reviewPeriods.every5years),
  };
  return labels[period as ReviewPeriod] ?? period;
}

export function getPreviousRelease(
  selectedRelease: DocumentDetailsRelease,
  releases: DocumentDetailsRelease[],
): {
  release: DocumentDetailsRelease;
  releaseNumber: number;
} | null {
  const currentIndex = releases.findIndex(
    (release) => release.id === selectedRelease.id,
  );

  if (currentIndex === -1 || currentIndex === releases.length - 1) {
    return null;
  }

  const previousRelease = releases[currentIndex + 1];
  if (!previousRelease) return null;

  return {
    release: previousRelease,
    releaseNumber: previousRelease.majorVersion,
  };
}

export function findDuplicateDocumentRelease({
  releases,
  title,
  categoryId,
}: {
  releases: DocumentReleaseSummary[] | undefined;
  title: string;
  categoryId: number | null;
}): DocumentReleaseSummary | undefined {
  if (!releases || categoryId === null) return undefined;
  const normalizedTitle = title.trim().toLowerCase();
  if (!normalizedTitle) return undefined;
  return releases.find(
    (release) =>
      release.document.category?.id === categoryId &&
      release.artifact.status !== DocumentArtifactStatus.archived &&
      release.artifact.title.trim().toLowerCase() === normalizedTitle,
  );
}
